import {
  calculateSurfaceByKrigingApi,
  calculateSurfaceByMimimalCurvatureApi,
  calculateSurfaceByMovingAverageApi,
  calculateSurfaceByRBFApi,
  calculateSurfaceBySequentialConvergenceApi,
  getInterpretationsIdsFromHorizonApi,
} from '@core/core/api'
import type { GridBuildFormData, GridBuildInfo, InterpretationData } from '@core/core/types'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { getFulfilledData } from 'common/helpers'
import type { CommonDomainType, HorizonId, ProjectId } from 'common/types'

import { selectActiveProjectId } from '../project'
import type { AppDispatch, RootState } from '../store'

export const calculateSurfaceBySCAction = createAsyncThunk<
  Promise<void>,
  GridBuildFormData,
  { state: RootState; dispatch: AppDispatch }
>('surfaceByScCalc', async (body, thunkAPI) => {
  const { projectId, horizons, domain } = await prepareData(thunkAPI.getState(), body)
  if (body.sequentialConvergence) {
    const buildInfo = getBuildInfo(projectId, horizons, body, domain)
    await calculateSurfaceBySequentialConvergenceApi(body.sequentialConvergence, buildInfo)
  }
})

export const calculateSurfaceByKrigingAction = createAsyncThunk<
  Promise<void>,
  GridBuildFormData,
  { state: RootState; dispatch: AppDispatch }
>('surfaceByKrigingCalc', async (body, thunkAPI) => {
  const { projectId, horizons, domain } = await prepareData(thunkAPI.getState(), body)
  if (body.kriging) {
    const buildInfo = getBuildInfo(projectId, horizons, body, domain)
    await calculateSurfaceByKrigingApi(body.kriging, buildInfo)
  }
})

export const calculateSurfaceByRBFAction = createAsyncThunk<
  Promise<void>,
  GridBuildFormData,
  { state: RootState; dispatch: AppDispatch }
>('surfaceByRBFCalc', async (body, thunkAPI) => {
  const { projectId, horizons, domain } = await prepareData(thunkAPI.getState(), body)
  if (body.kernel) {
    const buildInfo = getBuildInfo(projectId, horizons, body, domain)

    await calculateSurfaceByRBFApi(body.kernel, buildInfo)
  }
})

export const calculateSurfaceByMCAction = createAsyncThunk<
  Promise<void>,
  GridBuildFormData,
  { state: RootState; dispatch: AppDispatch }
>('surfaceByMCCalc', async (body, thunkAPI) => {
  const { projectId, horizons, domain } = await prepareData(thunkAPI.getState(), body)
  if (body.minimalCurvature) {
    const buildInfo = getBuildInfo(projectId, horizons, body, domain)

    await calculateSurfaceByMimimalCurvatureApi(body.minimalCurvature, buildInfo)
  }
})

export const calculateSurfaceByMAAction = createAsyncThunk<
  Promise<void>,
  GridBuildFormData,
  { state: RootState; dispatch: AppDispatch }
>('surfaceByMACalc', async (body, thunkAPI) => {
  const { projectId, horizons, domain } = await prepareData(thunkAPI.getState(), body)
  if (body.movingAverage) {
    const buildInfo = getBuildInfo(projectId, horizons, body, domain)

    await calculateSurfaceByMovingAverageApi(body.movingAverage, buildInfo)
  }
})

async function prepareData(state: RootState, body: GridBuildFormData) {
  const projectId = selectActiveProjectId(state)
  const { sources } = body.buildInfo
  const horizons = [...sources.interpretations2d, ...sources.interpretations3d]
  const domain = getDomain(sources)

  if (sources.horizons.length) {
    const extractionPromises = sources.horizons.map(horizon =>
      extractInterpretationsFromHorizon(horizon.id, projectId, horizon.weight),
    )

    const extractedInterpretations = await getFulfilledData(extractionPromises)
    extractedInterpretations.forEach(i => {
      horizons.push(...i)
    })
  }

  return { projectId, horizons, domain }
}

async function extractInterpretationsFromHorizon(
  horizonId: HorizonId,
  projectId: ProjectId,
  weight: number,
): Promise<InterpretationData[]> {
  const { horizon2dIds, horizon3dIds } = await getInterpretationsIdsFromHorizonApi(projectId, [
    horizonId,
  ])

  const interpretations2d: InterpretationData[] = horizon2dIds.map(id => {
    return {
      id,
      type: 'H2D',
      weight,
    }
  })
  const interpretations3d: InterpretationData[] = horizon3dIds.map(id => {
    return {
      id,
      type: 'H3D',
      weight,
    }
  })

  return [...interpretations2d, ...interpretations3d]
}

function getBuildInfo(
  projectId: ProjectId,
  horizons: InterpretationData[],
  { buildInfo }: GridBuildFormData,
  domain: CommonDomainType,
) {
  return {
    projectId,
    horizons,
    name: buildInfo.name,
    geometry: buildInfo.geometry,
    faultsId: buildInfo.faults,
    domain,
  }
}

function getDomain({ interpretations2d, interpretations3d }: GridBuildInfo['sources']) {
  return interpretations3d[0]?.domainType ?? interpretations2d[0]?.domainType ?? 'TWT'
}
